import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  type GetVendorInvoiceQueryType,
  type VendorInvoiceInterface,
  type VendorInvoiceResponseInterface,
  type VendorInvoicePaginationInterface,
  type InvoiceDetailInterface,
  type PODetailsInterface,
  type StatusActiveInvoicesInterface,
  type InvoiceReasonsType,
  type InvoicePoMatchingsResponseType,
  type InvoicePoMatchingDetailResponseType,
  type EditHistoryEntryType,
  type InvoiceActivityItemType,
} from 'features/Enterprise/Vendor/services/vendorInvoice'

export const SLICE_NAME = 'vendorInvoice'

interface VendorInvoiceStateInterface {
  isLoading: boolean
  queryParams: GetVendorInvoiceQueryType
  vendorInvoices: VendorInvoiceInterface[]
  vendorInvoicePagination: VendorInvoicePaginationInterface
  invoiceDetails: InvoiceDetailInterface
  poDetails: PODetailsInterface
  statusActiveInvoices: StatusActiveInvoicesInterface
  skuMatchData: Record<number, InvoicePoMatchingsResponseType>
  skuMatchFetchFailed: Record<number, boolean>
  matchingDetailData: Record<number, InvoicePoMatchingDetailResponseType>
  matchingDetailFetchFailed: Record<number, boolean>
  editHistoryData: Record<number, EditHistoryEntryType[]>
  invoiceActivities: InvoiceActivityItemType[]
  isActivitiesLoading: boolean
  invoiceReasonOptions: {
    INVOICE_REJECTION: string[]
    INVOICE_REVISION_SURAT_JALAN: string[]
    INVOICE_REVISION_FAKTUR_PAJAK: string[]
    INVOICE_REVISION_INVOICE: string[]
    INVOICE_REVISION_CONFIRMATION_LETTER: string[]
    INVOICE_REVISION_GRN: string[]
    INVOICE_REVISION_PO: string[]
    CHANGE_DUEDATE_REVISION: string[]
  }
}

const initialState: VendorInvoiceStateInterface = {
  isLoading: false,
  queryParams: {},
  vendorInvoices: [],
  vendorInvoicePagination: {
    next_cursor: '',
    prev_cursor: '',
  },
  invoiceDetails: {} as InvoiceDetailInterface,
  poDetails: {} as PODetailsInterface,
  statusActiveInvoices: {} as StatusActiveInvoicesInterface,
  skuMatchData: {},
  skuMatchFetchFailed: {},
  matchingDetailData: {},
  matchingDetailFetchFailed: {},
  editHistoryData: {},
  invoiceActivities: [],
  isActivitiesLoading: false,
  invoiceReasonOptions: {
    INVOICE_REJECTION: [],
    INVOICE_REVISION_SURAT_JALAN: [],
    INVOICE_REVISION_FAKTUR_PAJAK: [],
    INVOICE_REVISION_INVOICE: [],
    INVOICE_REVISION_CONFIRMATION_LETTER: [],
    INVOICE_REVISION_GRN: [],
    INVOICE_REVISION_PO: [],
    CHANGE_DUEDATE_REVISION: [],
  },
}

const vendorInvoiceSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setVendorInvoices: (state, action: PayloadAction<VendorInvoiceResponseInterface>) => {
      state.vendorInvoices = action.payload.data.invoices
      state.vendorInvoicePagination = action.payload.pagination
    },
    setQueryParams: (state, action: PayloadAction<GetVendorInvoiceQueryType>) => {
      state.queryParams = action.payload
    },
    setInvoiceDetails: (state, action: PayloadAction<InvoiceDetailInterface>) => {
      state.invoiceDetails = action.payload
    },
    setPODetails: (state, action: PayloadAction<PODetailsInterface>) => {
      state.poDetails = action.payload
    },
    setStatusActiveInvoices: (state, action: PayloadAction<StatusActiveInvoicesInterface>) => {
      state.statusActiveInvoices = action.payload
    },
    setSkuMatchData: (
      state,
      action: PayloadAction<{
        purchaseOrderId: number
        data: InvoicePoMatchingsResponseType
      }>,
    ) => {
      state.skuMatchData[action.payload.purchaseOrderId] = action.payload.data
      delete state.skuMatchFetchFailed[action.payload.purchaseOrderId]
    },
    setSkuMatchFetchFailed: (
      state,
      action: PayloadAction<{ purchaseOrderId: number; failed: boolean }>,
    ) => {
      if (action.payload.failed) {
        state.skuMatchFetchFailed[action.payload.purchaseOrderId] = true
        delete state.skuMatchData[action.payload.purchaseOrderId]
      } else {
        delete state.skuMatchFetchFailed[action.payload.purchaseOrderId]
      }
    },
    setMatchingDetailData: (
      state,
      action: PayloadAction<{
        invoicePurchaseOrderId: number
        data: InvoicePoMatchingDetailResponseType
      }>,
    ) => {
      state.matchingDetailData[action.payload.invoicePurchaseOrderId] = action.payload.data
      delete state.matchingDetailFetchFailed[action.payload.invoicePurchaseOrderId]
    },
    setMatchingDetailFetchFailed: (
      state,
      action: PayloadAction<{ invoicePurchaseOrderId: number; failed: boolean }>,
    ) => {
      if (action.payload.failed) {
        state.matchingDetailFetchFailed[action.payload.invoicePurchaseOrderId] = true
        delete state.matchingDetailData[action.payload.invoicePurchaseOrderId]
      } else {
        delete state.matchingDetailFetchFailed[action.payload.invoicePurchaseOrderId]
      }
    },
    setEditHistoryData: (
      state,
      action: PayloadAction<{
        invoicePurchaseOrderId: number
        entries: EditHistoryEntryType[]
      }>,
    ) => {
      state.editHistoryData[action.payload.invoicePurchaseOrderId] = action.payload.entries
    },
    setInvoiceActivities: (state, action: PayloadAction<InvoiceActivityItemType[]>) => {
      state.invoiceActivities = action.payload
    },
    setActivitiesLoading: (state, action: PayloadAction<boolean>) => {
      state.isActivitiesLoading = action.payload
    },
    resetSkuMatchState: (state) => {
      state.skuMatchData = {}
      state.skuMatchFetchFailed = {}
      state.matchingDetailData = {}
      state.matchingDetailFetchFailed = {}
      state.editHistoryData = {}
    },
    setInvoiceReasonOptions: (
      state,
      action: PayloadAction<{ type: string; reasons: InvoiceReasonsType[] }>,
    ) => {
      state.invoiceReasonOptions = {
        ...state.invoiceReasonOptions,
        [action.payload.type]: action.payload.reasons.map((reason) => reason.reason),
      }
    },
  },
})

export const {
  setLoading,
  setVendorInvoices,
  setQueryParams,
  setInvoiceDetails,
  setPODetails,
  setStatusActiveInvoices,
  setSkuMatchData,
  setSkuMatchFetchFailed,
  setMatchingDetailData,
  setMatchingDetailFetchFailed,
  setEditHistoryData,
  setInvoiceActivities,
  setActivitiesLoading,
  resetSkuMatchState,
  setInvoiceReasonOptions,
} = vendorInvoiceSlice.actions

export default vendorInvoiceSlice.reducer
